/**
 * mine.mOre - Windows 98 Edition
 * Retro-themed server-side mining dashboard
 * Build: 2025-12-04
 */

import { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { PrivyProvider, usePrivy } from '@privy-io/react-auth';
import {
  toSolanaWalletConnectors,
  defaultSolanaRpcsPlugin,
} from '@privy-io/react-auth/solana';
import { createSolanaRpc, createSolanaRpcSubscriptions } from '@solana/kit';
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import type { Adapter } from '@solana/wallet-adapter-base';
import '@solana/wallet-adapter-react-ui/styles.css';
import { SunsetApp } from './sunset/SunsetApp';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import {
  createDefaultAuthorizationCache,
  createDefaultChainSelector,
  createDefaultWalletNotFoundHandler,
  registerMwa,
} from '@solana-mobile/wallet-standard-mobile';
import { Dashboard } from './components/Dashboard';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { Leaderboard } from './components/Leaderboard';
import { SettingsPage } from './components/SettingsPage';
import { PortfolioPage } from './components/PortfolioPage';
import { WalletPage } from './components/WalletPage';
import { InstallBanner } from './components/InstallBanner';
import { HelpButton } from './components/HelpButton';
import { AnalyticsPlaceholder } from './components/analytics/AnalyticsPlaceholder';
import { MotherlodeAnalytics } from './components/analytics/MotherlodeAnalytics';
import { MotherlodeRoundDetail } from './components/analytics/MotherlodeRoundDetail';
import { EmissionsAnalytics } from './components/analytics/EmissionsAnalytics';
import { PriceChartsAnalytics } from './components/analytics/PriceChartsAnalytics';
import { LiquidStakingDashboard } from './components/staking/LiquidStakingDashboard';
import { MeteoraDLMMDashboard } from './components/meteora/MeteoraDLMMDashboard';
import { MeteoraLeaderboard } from './components/MeteoraLeaderboard';
import { TokenStackerDashboard } from './components/token-stacker/TokenStackerDashboard';
import { FLPDashboard } from './components/flp/FLPDashboard';
import { TeamPage } from './components/blog/TeamPage';
import { LibraryPage } from './components/blog/LibraryPage';
import { SocialPage } from './components/SocialPage';
import { StatusPage } from './components/status/StatusPage';
import { SEO } from './components/SEO';
import { EasyStartLayout } from './components/easystart/EasyStartLayout';
import { CompetitionPage } from './components/CompetitionPage';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AccountProvider } from './context/AccountContext';
import { BalanceProvider } from './context/BalanceContext';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import './win98.css';

// MWA protocol is Android-Chrome only (per Solana Mobile docs: "MWA is not
// available on any iOS browser"). Registering on iOS leaves users with a
// dead-end tile, so gate strictly on Android.
const isAndroid = typeof window !== 'undefined' && /Android/i.test(navigator.userAgent);

// Use system font stack (SF Pro) in Firefox/Safari where it renders well.
// Chromium/Brave get the Win98 stack via CSS variable default.
const isFirefoxOrSafari = typeof navigator !== 'undefined' &&
  (/Firefox/i.test(navigator.userAgent) ||
   (/Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent)));
if (isFirefoxOrSafari) {
  document.documentElement.classList.add('mm-gecko-webkit');
}
if (isAndroid) {
  registerMwa({
    appIdentity: {
      name: 'mine.mOre',
      uri: typeof window !== 'undefined' ? window.location.origin : 'https://minemore.app',
      icon: '/favicon.ico',
    },
    authorizationCache: createDefaultAuthorizationCache(),
    chains: ['solana:mainnet'],
    chainSelector: createDefaultChainSelector(),
    onWalletNotFound: createDefaultWalletNotFoundHandler(),
  });
}

// Privy's own docs warn: "some Solana wallet connectors do not gracefully
// support autoConnect." With MWA registered at module load on Android,
// autoConnect can fire the MWA handshake before any user gesture, burning
// Chrome's gesture budget so the subsequent tap on the MWA tile can't launch
// the `solana-wallet:` intent and hangs on a spinner. Keep it off.
const solanaConnectors = toSolanaWalletConnectors({
  shouldAutoConnect: false,
  walletConnectCloudProjectId: '67d3928476d888179a1a0d7418d9213d',
});

// 'wallet_connect' surfaces Phantom/Solflare (and others) through the WC v2
// registry deeplink. Unlike Phantom's/Solflare's native "browse" universal
// links — which open the dapp inside the wallet's in-app WebView and strand
// the session in that WebView's cookie jar — WC deeplinks into the wallet
// *app* and relays the signature back over WebSocket, so the session lands
// in the original browser tab. Added alongside the existing tiles so testers
// can try it without removing the current paths.
const walletList: ('phantom' | 'solflare' | 'backpack' | 'detected_solana_wallets' | 'wallet_connect')[] =
  ['phantom', 'solflare', 'backpack', 'detected_solana_wallets', 'wallet_connect'];

// RPC Configuration — supplied via env (never hardcode provider keys here; anything
// VITE_* ships in the bundle, so the key must be origin-restricted on the provider side).
// Falls back to the public endpoint so a missing env var degrades rather than breaks.
const HELIUS_RPC =
  (import.meta.env.VITE_RPC_URL as string | undefined) || 'https://api.mainnet-beta.solana.com';
const HELIUS_WSS =
  (import.meta.env.VITE_RPC_WSS_URL as string | undefined) || HELIUS_RPC.replace(/^http/, 'ws');

// Admin route wrapper without Privy
function AdminRoute() {
  return (
    <ThemeProvider>
      <AdminDashboard />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Competition route wrapper - public, Privy optional for user rank highlight
function CompetitionRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <CompetitionPage />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Analytics route wrappers - public, but needs AccountProvider for user account highlighting
function AnalyticsMotherlodeRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <MotherlodeAnalytics />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function MotherlodeRoundDetailRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <MotherlodeRoundDetail />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function TeamRoute() {
  return (
    <ThemeProvider>
      <TeamPage />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function StatusRoute() {
  return (
    <ThemeProvider>
      <StatusPage />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function LibraryRoute() {
  return (
    <ThemeProvider>
      <LibraryPage />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function SocialRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <SocialPage />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function AnalyticsEmissionsRoute() {
  return (
    <ThemeProvider>
      <EmissionsAnalytics />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function AnalyticsYieldsRoute() {
  return (
    <ThemeProvider>
      <SEO
        title="Yield Analytics — MineMore | Coming Soon"
        description="Yield analytics for ORE mining on Solana. Coming soon."
        path="/analytics/yields"
      />
      <AnalyticsPlaceholder title="Yields" />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

function AnalyticsPricesRoute() {
  return (
    <ThemeProvider>
      <PriceChartsAnalytics />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Leaderboard route wrapper - needs PrivyProvider and AccountProvider for copy settings feature
function LeaderboardRoute() {
  const navigate = useNavigate();
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <div className="win98-desktop" style={{ padding: '8px', overflowY: 'auto' }}>
              <Leaderboard onBackToHome={() => navigate('/')} />
            </div>
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}


// Settings route wrapper - needs PrivyProvider and AccountProvider for authentication
function SettingsRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <SettingsPage />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Portfolio route wrapper - needs PrivyProvider and AccountProvider for authentication
function PortfolioRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <PortfolioPage />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Staking route wrapper - needs PrivyProvider for optional auth (stake/unstake)
function StakingRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <div className="win98-desktop" style={{ justifyContent: 'flex-start' }}>
              <LiquidStakingDashboard />
            </div>
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Meteora DLMM route wrapper - needs PrivyProvider for auth (deposit/withdraw/claim)
function MeteoraDLMMRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <div className="win98-desktop" style={{ justifyContent: 'flex-start' }}>
              <MeteoraDLMMDashboard />
            </div>
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// DLMM Leaderboard route wrapper - needs PrivyProvider for optional auth (own-entry highlighting)
function DLMMLeaderboardRoute() {
  const navigate = useNavigate();
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <div className="win98-desktop" style={{ padding: '8px', overflowY: 'auto' }}>
              <MeteoraLeaderboard onBackToHome={() => navigate('/')} />
            </div>
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Token Stacker route wrapper - needs PrivyProvider for auth (deposit/enable/stop)
function TokenStackerRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <div className="win98-desktop">
              <TokenStackerDashboard />
            </div>
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// FLP8 route wrapper - needs PrivyProvider for auth (deposit/withdraw)
function FLP8Route() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <div className="win98-desktop">
              <FLPDashboard />
            </div>
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Wallet route wrapper - needs PrivyProvider and AccountProvider for authentication
function WalletRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
          solana: {
            rpcs: {
              'solana:mainnet': {
                rpc: createSolanaRpc(HELIUS_RPC),
                rpcSubscriptions: createSolanaRpcSubscriptions(HELIUS_WSS),
              },
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <WalletPage />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// EasyStart route wrapper - beginner onboarding flow
function EasyStartRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
          solana: {
            rpcs: {
              'solana:mainnet': {
                rpc: createSolanaRpc(HELIUS_RPC),
                rpcSubscriptions: createSolanaRpcSubscriptions(HELIUS_WSS),
              },
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <EasyStartLayout />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// Login and Signup routes - redirect to home (login now opens Privy directly from landing page)
function LoginRoute() {
  return <Navigate to="/" replace />;
}

function SignupRoute() {
  return <Navigate to="/" replace />;
}

// Inner component that handles the main route logic (needs PrivyProvider context)
function MainRouteInner() {
  const navigate = useNavigate();
  const { authenticated } = usePrivy();

  const handleBackToSelector = () => {
    navigate('/login');
  };

  // Show Dashboard (handles both authenticated and non-authenticated states)
  return (
    <Dashboard
      onBackToSelector={handleBackToSelector}
    />
  );
}

// Main route wrapper with Privy
function MainRoute() {
  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
          solana: {
            rpcs: {
              'solana:mainnet': {
                rpc: createSolanaRpc(HELIUS_RPC),
                rpcSubscriptions: createSolanaRpcSubscriptions(HELIUS_WSS),
              },
            },
          },
        }}
      >
        <AccountProvider>
          <BalanceProvider>
            <MainRouteInner />
          </BalanceProvider>
        </AccountProvider>
      </PrivyProvider>
      <InstallBanner />
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

/**
 * Analytics tracker component
 * Must be rendered inside BrowserRouter to access useLocation
 */
function AnalyticsTracker() {
  useGoogleAnalytics();
  return null;
}

// Sunset route: serves the wind-down withdraw UI from /sunset (test) and, when
// VITE_SUNSET_MODE is enabled, from every route on the site. See src/sunset/
// and CLAUDE.md for the architecture. Uses the same Privy app ID / appUrl as
// the main app so existing users' embedded wallets resolve identically; the
// only behavioral delta is embeddedWallets.solana.createOnLogin = 'off' so
// new signups don't get a brand-new (and useless) wallet minted during sunset.
const SUNSET_RPC_URL = HELIUS_RPC;

function SunsetRoute() {
  // Empty wallets array — Phantom, Solflare, Backpack, and MWA (registered at
  // module load above for Android) all advertise themselves as Wallet-Standard
  // wallets and are auto-discovered by WalletProvider. No explicit adapter
  // classes needed, which keeps us off @solana/wallet-adapter-wallets.
  const adapters = useMemo<Adapter[]>(() => [], []);

  return (
    <ThemeProvider>
      <PrivyProvider
        appId={import.meta.env.VITE_PRIVY_APP_ID}
        config={{
          appUrl: 'https://minemore.app',
          loginMethods: ['email', 'google', 'twitter', 'discord', 'wallet'],
          appearance: {
            theme: 'light',
            accentColor: '#000080',
            walletChainType: 'solana-only',
            walletList,
          },
          // Required for Privy's useSignTransaction on Solana when not using
          // the broader Privy v3 cross-chain plugin set.
          plugins: [defaultSolanaRpcsPlugin()],
          // Sunset never creates new wallets. Existing MineMore users' embedded
          // wallets continue to resolve via linkedAccounts; new sign-ins get an
          // empty wallet list, which useWallet.ts surfaces as a clear error.
          embeddedWallets: {
            solana: {
              createOnLogin: 'off',
            },
          },
          externalWallets: {
            solana: {
              connectors: solanaConnectors,
            },
          },
        }}
      >
        <ConnectionProvider endpoint={SUNSET_RPC_URL}>
          <WalletProvider wallets={adapters} autoConnect>
            <WalletModalProvider>
              <SunsetApp />
            </WalletModalProvider>
          </WalletProvider>
        </ConnectionProvider>
      </PrivyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

// VITE_SUNSET_MODE=true flips the entire site to sunset mode — every URL serves
// SunsetRoute, mining/portfolio/analytics become unreachable. Defaults to false
// so the flag can ship code-complete and be flipped via env-var-only redeploy
// when the actual shutdown happens.
const SUNSET_MODE = import.meta.env.VITE_SUNSET_MODE === 'true';

// App wrapper with routing
function App() {
  return (
    <LanguageProvider>
    <BrowserRouter>
      <AnalyticsTracker />
      {!SUNSET_MODE && <HelpButton />}
      {SUNSET_MODE ? (
        <Routes>
          {/* Sunset mode: / renders the withdraw site, every other path
              redirects there so stale bookmarks (/portfolio, /wallet, etc.)
              don't leave the address bar pointing at a route that no longer
              exists. Replace history so the back button doesn't cycle. */}
          <Route path="/" element={<SunsetRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <Routes>
          {/* Always-on preview route so /sunset-preview can be tested on prod
              without flipping VITE_SUNSET_MODE for real users. */}
          <Route path="/sunset-preview" element={<SunsetRoute />} />
          <Route path="/admin/*" element={<AdminRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/signup" element={<SignupRoute />} />
          <Route path="/leaderboard" element={<LeaderboardRoute />} />
          <Route path="/competition" element={<CompetitionRoute />} />
          <Route path="/dlmmleaderboard" element={<DLMMLeaderboardRoute />} />
          <Route path="/settings" element={<SettingsRoute />} />
          <Route path="/portfolio" element={<PortfolioRoute />} />
          <Route path="/analytics/motherlodes/:roundNumber" element={<MotherlodeRoundDetailRoute />} />
          <Route path="/wallet" element={<WalletRoute />} />
          <Route path="/analytics/motherlodes" element={<AnalyticsMotherlodeRoute />} />
          <Route path="/analytics/emissions" element={<AnalyticsEmissionsRoute />} />
          <Route path="/analytics/yields" element={<AnalyticsYieldsRoute />} />
          <Route path="/analytics/prices" element={<AnalyticsPricesRoute />} />
          <Route path="/staking" element={<StakingRoute />} />
          <Route path="/dlmm" element={<MeteoraDLMMRoute />} />
          <Route path="/compoundore" element={<TokenStackerRoute />} />
          <Route path="/flp8" element={<FLP8Route />} />
          <Route path="/team" element={<TeamRoute />} />
          <Route path="/library" element={<LibraryRoute />} />
          <Route path="/status" element={<StatusRoute />} />
          <Route path="/social" element={<SocialRoute />} />
          <Route path="/easystart" element={<EasyStartRoute />} />
          <Route path="/easystart/home" element={<EasyStartRoute />} />
          <Route path="/easystart/topup" element={<EasyStartRoute />} />
          <Route path="/easystart/dashboard" element={<EasyStartRoute />} />
          <Route path="/mining" element={<MainRoute />} />
          <Route path="/" element={<MainRoute />} />
        </Routes>
      )}
    </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
